// src/components/cofounder/CoFounderSettings.js
import React, { useEffect, useState } from 'react';
import { Settings, X, Plus, Trash2, Play } from 'lucide-react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useCoFounder } from '../../context/CoFounderContext';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';

const CRON_PRESETS = [
  { label: 'Every day at 08:00', value: '0 8 * * *' },
  { label: 'Every Monday at 09:00', value: '0 9 * * 1' },
  { label: 'Every 1st of month at 07:00', value: '0 7 1 * *' }
];

const getDefaultConfig = (config) => ({
  provider: config?.provider || 'cloud-function',
  persona: config?.persona || 'professional',
  enableProactiveInsights: config?.enableProactiveInsights ?? true,
  enabledToolCategories: config?.enabledToolCategories || ['read', 'write'],
  cronJobs: Array.isArray(config?.cronJobs) ? config.cronJobs : []
});

const isValidCronExpression = (expr) => {
  const normalized = (expr || '').trim();
  return /^(\S+\s+){4}\S+$/.test(normalized);
};

const CoFounderSettings = ({ isOpen, onClose }) => {
  const { agentConfig, setAgentConfig, sendMessage } = useCoFounder();
  const { currentUser } = useAuth();

  const [localConfig, setLocalConfig] = useState(getDefaultConfig(agentConfig));
  const [isSaving, setIsSaving] = useState(false);
  const [cronError, setCronError] = useState('');
  const [newCronJob, setNewCronJob] = useState({
    name: '',
    schedule: '0 8 * * *',
    prompt: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    enabled: true
  });

  useEffect(() => {
    if (isOpen) {
      setLocalConfig(getDefaultConfig(agentConfig));
      setCronError('');
    }
  }, [agentConfig, isOpen]);

  if (!isOpen) return null;

  const updateCronJob = (index, updates) => {
    setLocalConfig((prev) => {
      const nextJobs = [...(prev.cronJobs || [])];
      nextJobs[index] = { ...nextJobs[index], ...updates };
      return { ...prev, cronJobs: nextJobs };
    });
  };

  const removeCronJob = (index) => {
    setLocalConfig((prev) => ({
      ...prev,
      cronJobs: (prev.cronJobs || []).filter((_, i) => i !== index)
    }));
  };

  const addCronJob = () => {
    if (!newCronJob.name.trim()) {
      setCronError('Job name is required.');
      return;
    }
    if (!newCronJob.prompt.trim()) {
      setCronError('Execution prompt is required.');
      return;
    }
    if (!isValidCronExpression(newCronJob.schedule)) {
      setCronError('Cron expression must have 5 parts, e.g. "0 8 * * *".');
      return;
    }

    const job = {
      id: `cron_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: newCronJob.name.trim(),
      schedule: newCronJob.schedule.trim(),
      prompt: newCronJob.prompt.trim(),
      timezone: newCronJob.timezone || 'UTC',
      enabled: Boolean(newCronJob.enabled),
      createdAt: new Date().toISOString()
    };

    setLocalConfig((prev) => ({
      ...prev,
      cronJobs: [...(prev.cronJobs || []), job]
    }));
    setNewCronJob((prev) => ({ ...prev, name: '', prompt: '' }));
    setCronError('');
  };

  const runCronJobNow = async (job, index) => {
    const now = new Date().toISOString();

    updateCronJob(index, {
      lastRunAt: now,
      lastRunStatus: 'running'
    });

    try {
      await sendMessage(`Scheduled job execution: "${job.name}"\n\n${job.prompt}`);
      updateCronJob(index, { lastRunStatus: 'success' });
    } catch (e) {
      updateCronJob(index, { lastRunStatus: 'failed', lastError: e.message || 'Execution failed' });
    }
  };

  const handleSave = async () => {
    const normalizedConfig = getDefaultConfig(localConfig);
    setIsSaving(true);
    setAgentConfig(normalizedConfig);

    if (currentUser?.uid) {
      try {
        await setDoc(
          doc(db, 'cofounderConfig', currentUser.uid),
          {
            userId: currentUser.uid,
            ...normalizedConfig,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      } catch (e) {
        console.error('Error saving coFounder settings:', e);
      }
    }

    setIsSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Settings size={18} className="text-[#190192]" />
            <h3 className="font-semibold text-gray-800">coFounder Settings</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-132px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">AI Provider</label>
              <select
                value={localConfig.provider}
                onChange={(e) => setLocalConfig({ ...localConfig, provider: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
              >
                <option value="gemini">Gemini 2.0 Flash</option>
                <option value="cloud-function">Cloud Function (Server-side)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personality Style</label>
              <select
                value={localConfig.persona}
                onChange={(e) => setLocalConfig({ ...localConfig, persona: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly & Casual</option>
                <option value="concise">Concise & Direct</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-gray-200 p-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Proactive Insights</p>
              <p className="text-xs text-gray-500">Get automatic alerts about your business</p>
            </div>
            <button
              onClick={() => setLocalConfig({ ...localConfig, enableProactiveInsights: !localConfig.enableProactiveInsights })}
              className={`w-10 h-6 rounded-full transition-colors ${
                localConfig.enableProactiveInsights ? 'bg-[#190192]' : 'bg-gray-300'
              }`}
            >
              <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${
                localConfig.enableProactiveInsights ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-800">Cron Jobs</h4>
              <p className="text-xs text-gray-500">Set up recurring coFounder jobs and execute them on demand.</p>
            </div>

            <div className="space-y-3 mb-4">
              <input
                type="text"
                placeholder="Job name (e.g., Weekly cashflow review)"
                value={newCronJob.name}
                onChange={(e) => setNewCronJob((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Cron expression (e.g., 0 8 * * *)"
                  value={newCronJob.schedule}
                  onChange={(e) => setNewCronJob((prev) => ({ ...prev, schedule: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
                />
                <select
                  value={newCronJob.schedule}
                  onChange={(e) => setNewCronJob((prev) => ({ ...prev, schedule: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
                >
                  {CRON_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                  ))}
                </select>
              </div>

              <textarea
                rows="3"
                placeholder="Execution prompt (what coFounder should do when this job runs)"
                value={newCronJob.prompt}
                onChange={(e) => setNewCronJob((prev) => ({ ...prev, prompt: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
              />

              <div className="flex justify-between items-center">
                <input
                  type="text"
                  placeholder="Timezone (e.g., Africa/Johannesburg)"
                  value={newCronJob.timezone}
                  onChange={(e) => setNewCronJob((prev) => ({ ...prev, timezone: e.target.value }))}
                  className="w-full max-w-xs border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#190192] focus:ring-1 focus:ring-[#190192]"
                />
                <button
                  type="button"
                  onClick={addCronJob}
                  className="ml-3 inline-flex items-center gap-1 px-3 py-2 text-sm text-white bg-[#190192] rounded-xl hover:bg-[#0f0075]"
                >
                  <Plus size={14} />
                  Add Job
                </button>
              </div>

              {cronError && <p className="text-xs text-red-600">{cronError}</p>}
            </div>

            <div className="space-y-3">
              {(localConfig.cronJobs || []).length === 0 && (
                <p className="text-xs text-gray-500">No cron jobs configured yet.</p>
              )}

              {(localConfig.cronJobs || []).map((job, index) => (
                <div key={job.id || `${job.name}-${index}`} className="rounded-xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">{job.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5 break-all">{job.schedule} ({job.timezone || 'UTC'})</p>
                      <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{job.prompt}</p>
                      {job.lastRunAt && (
                        <p className="text-[11px] text-gray-500 mt-2">
                          Last run: {new Date(job.lastRunAt).toLocaleString()} ({job.lastRunStatus || 'unknown'})
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => updateCronJob(index, { enabled: !job.enabled })}
                        className={`w-10 h-6 rounded-full transition-colors ${job.enabled ? 'bg-[#190192]' : 'bg-gray-300'}`}
                        title={job.enabled ? 'Disable job' : 'Enable job'}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${job.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => runCronJobNow(job, index)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        title="Run now"
                      >
                        <Play size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeCronJob(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete job"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm text-white bg-[#190192] rounded-xl hover:bg-[#0f0075] disabled:opacity-60"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoFounderSettings;
